// import node_modules
import * as PropTypes from 'prop-types';
import * as React from 'react';
import { Heading, Spacer, Text } from '@storylinerlabs/design';
import { useTranslation } from 'react-i18next';

// import components
import Column from '../atoms/Column';
import Content from '../atoms/Content';
import LogosGrid from '../grids/LogosGrid';
import MainLayout from '../layouts/MainLayout';
import NewsletterSection from '../elements/NewsletterSection';
import SEO from '../meta/SEO';
import Section from '../atoms/Section';
import StorylinerImage from '../atoms/StorylinerImage';

// define page component
const AboutTemplate: React.FunctionComponent<{ hrefLang: string }> = ({ hrefLang }) => {
  // init hooks
  const { t } = useTranslation('about');

  // init render
  return (
    <>
      {/* SEO */}
      <SEO
        description={t('seo.description')}
        hrefLang={hrefLang}
        image={t('seo.image')}
        title={t('seo.title')}
      />

      <MainLayout>
        {/* Hero */}
        <Section>
          {/* Navbar */}
          <Spacer height="64px" />
          <Spacer mobileHeight="25px" tabletHeight="50px" height="75px" />
        </Section>
        <Section>
          <Column cols="ml">
            <Heading alignment="center" tag="h1" isHyphenated={false}>
              {t('hero.title')}
            </Heading>
          </Column>
        </Section>
        <Section>
          <Spacer mobileHeight="16px" tabletHeight="16px" height="16px" />
        </Section>
        <Section>
          <Column>
            <StorylinerImage
              imageId={t('team.image.src')}
              height={611}
              title={t('team.image.alt')}
              width={1438}
            />
          </Column>
        </Section>
        <Section>
          <Spacer mobileHeight="16px" tabletHeight="16px" height="16px" />
        </Section>

        {/* Brand Story */}
        <Section>
          <Column cols="md">
            <Content>{t('brandStory')}</Content>
          </Column>
        </Section>
        <Section>
          <Spacer height="50px" />
        </Section>

        {/* Partner */}
        <Section bgColor="allports">
          <Spacer mobileHeight="50px" tabletHeight="50px" height="50px" />
        </Section>
        <Section bgColor="allports">
          <Column>
            <Heading alignment="center" tag="label" isWhite>
              {t('partners.title')}
            </Heading>
          </Column>
        </Section>
        <LogosGrid bgColor="allports" logos={t('partners.logos', { returnObjects: true })} />
        <Section
          bgImage={t('partners.dividerBottom')}
          bgImageDark={t('partners.dividerBottomDark')}
          bgPosition="center bottom"
          zIndex={0}
        >
          <Spacer mobileHeight="100px" tabletHeight="125px" height="150px" />
        </Section>

        {/* Jobs */}
        <Section>
          <Spacer mobileHeight="50px" tabletHeight="50px" height="75px" />
        </Section>
        <Section sectionId="jobs">
          <Column cols="md">
            <Heading alignment="center" tag="h2" isHyphenated={false}>
              {t('jobs.title')}
            </Heading>
            <Text alignment="center">{t('jobs.text')}</Text>
          </Column>
        </Section>
        <Section>
          <Column cols="md">
            <Text alignment="center" isItalic>
              {t('jobs.noJobs')}
            </Text>
          </Column>
        </Section>
        <Section>
          <Spacer mobileHeight="50px" tabletHeight="50px" height="75px" />
        </Section>

        {/* Call to action */}
        <Section bgColor="desertStorm">
          <Spacer height="100px" />
        </Section>
        <NewsletterSection bgColor="desertStorm" />
        <Section bgColor="desertStorm">
          <Spacer height="100px" />
        </Section>
        <Section>
          <Spacer height="50px" />
        </Section>
      </MainLayout>
    </>
  );
};

AboutTemplate.propTypes = {
  hrefLang: PropTypes.string.isRequired,
};

export default AboutTemplate;
